import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageLoader,
  PageContent,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
} from '../../components';
import { useOrganization } from './hooks/useOrganization';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { OrganizationMetricsComponent } from './components/OrganizationMetrics';
import { OrganizationProfileComponent } from './components/OrganizationProfile';
import { DeleteOrganizationModal } from './components/DeleteOrganizationModal';

export const Organization: React.FC = () => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    profile,
    isLoading,
    isSaving,
    isEditing,
    error,
    fieldErrors,
    updateProfile,
    startEditing,
    cancelEditing,
    saveProfile,
    deleteOrganization,
  } = useOrganization();

  const showLoader = useMinimumLoader(isLoading, { minimumDuration: 500 });

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (!profile) {
    return (
      <PageContent>
        <div className="space-y-6">
          <div className="text-center py-12">
            <span className="material-icons text-6xl text-gray-400 mb-4">business</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('organization.noOrganizationFound')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">{t('organization.unableToLoadData')}</p>
          </div>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('organization.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('organization.subtitle')}</p>
        </div>

        <OrganizationMetricsComponent profile={profile} />
        <OrganizationProfileComponent
          profile={profile}
          isEditing={isEditing}
          isSaving={isSaving}
          fieldErrors={fieldErrors}
          onUpdateProfile={updateProfile}
          onStartEditing={startEditing}
          onCancelEditing={cancelEditing}
          onSaveProfile={saveProfile}
        />

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="material-icons text-red-500 mr-2">error</span>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        <Card className="border-red-200 dark:border-red-800">
          <CardHeader>
            <CardTitle className="text-red-600 dark:text-red-400">
              {t('organization.dangerZone.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('organization.dangerZone.deleteTitle')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {t('organization.dangerZone.deleteDescription')}
                </p>
              </div>
              <Button onClick={() => setShowDeleteModal(true)} variant="danger" className="ml-4">
                <span className="material-icons mr-2">delete</span>
                {t('organization.dangerZone.deleteButton')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <DeleteOrganizationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          organizationName={profile?.name || 'Organization'}
          onDelete={deleteOrganization}
        />
      </div>
    </PageContent>
  );
};
